import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { Board } from '@cortex/domain-org-issues';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useCreateOrgBoard } from '../../../hooks/useOrgIssues.js';
import type { OrgBoardView } from '../../../lib/api/org-issues.js';

/**
 * 画面から指定できる所有組織の種類。人を指定した所有（`users`）は MCP から作る
 * （人の候補を出す前提のボードがまだ無いため）。
 * @graph-connects none
 */
const OWNER_KINDS = ['team', 'group', 'division'] as const;

/** 画面から指定できる公開範囲。人の指定（`users`）は MCP から。 @graph-connects none */
export const VISIBILITY_KINDS = ['leaders', 'team', 'group', 'division', 'company'] as const;

/** 画面から指定できる公開範囲の種類。 @graph-connects none */
export type VisibilityKind = (typeof VISIBILITY_KINDS)[number];

/** 所有組織の表示。 @graph-connects none */
export function ownerText(board: Board, t: (key: string) => string): string {
  const kind = t(`orgIssues.ownerKind.${board.owner.kind}`);
  return board.owner.kind === 'users' ? kind : `${kind}: ${board.owner.code}`;
}

/**
 * 見られるボードの一覧。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 自分が見られる組織課題ボードを、所有組織・公開範囲・自分が編集者かと一緒に並べ、看板へ移る入口にする
 * @graph-connects none
 */
export function BoardCards({ boards }: { boards: readonly OrgBoardView[] }): ReactNode {
  const { t } = useTranslation();
  if (boards.length === 0) {
    return <p className="text-sm text-[var(--color-text-muted)]">{t('orgIssues.boards.empty')}</p>;
  }
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {boards.map(({ board, permissions }) => (
        <Card key={board.id}>
          <CardContent className="space-y-2 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold text-[var(--color-text-primary)]">{board.name}</p>
              {permissions.canManage && (
                <Badge variant="info">{t('orgIssues.boards.editor')}</Badge>
              )}
            </div>
            <p className="text-xs text-[var(--color-text-muted)]">
              {t('orgIssues.boards.owner')}: {ownerText(board, t)} /{' '}
              {t('orgIssues.boards.visibility')}:{' '}
              {t(`orgIssues.visibility.${board.visibility.kind}`)}
            </p>
            <Link to="/org-issues/$boardId" params={{ boardId: board.id }}>
              <Button type="button" size="sm">
                {t('orgIssues.boards.open')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * ボードを作るフォーム。作れるのは所有組織のリーダーと admin だけで、判定と文言は API が持つ
 * （ここでは絞らない。弾かれたら API の文言をそのまま出す）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題ボードを名前・所有組織・公開範囲を指定して作り、作ったボードの看板へ移る
 * @graph-connects useCreateOrgBoard [calls] ボードの作成
 */
export function CreateBoardForm({ onCancel }: { onCancel: () => void }): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateOrgBoard();
  const [name, setName] = useState('');
  const [ownerKind, setOwnerKind] = useState<(typeof OWNER_KINDS)[number]>('team');
  const [ownerCode, setOwnerCode] = useState('');
  const [visibility, setVisibility] = useState<VisibilityKind>('team');

  return (
    <Card>
      <CardContent className="p-4">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(
              {
                name,
                owner: { kind: ownerKind, code: ownerCode },
                visibility: { kind: visibility },
              },
              {
                onSuccess: (view) => {
                  void navigate({ to: '/org-issues/$boardId', params: { boardId: view.board.id } });
                },
              },
            );
          }}
        >
          <div className="grid gap-3 md:grid-cols-2">
            <Input
              label={t('orgIssues.createBoard.name')}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
            <Select
              label={t('orgIssues.createBoard.visibility')}
              value={visibility}
              options={VISIBILITY_KINDS.map((kind) => ({
                value: kind,
                label: t(`orgIssues.visibility.${kind}`),
              }))}
              onChange={(e) => {
                const next = VISIBILITY_KINDS.find((kind) => kind === e.target.value);
                if (next !== undefined) setVisibility(next);
              }}
            />
            <Select
              label={t('orgIssues.createBoard.ownerKind')}
              value={ownerKind}
              options={OWNER_KINDS.map((kind) => ({
                value: kind,
                label: t(`orgIssues.ownerKind.${kind}`),
              }))}
              onChange={(e) => {
                const next = OWNER_KINDS.find((kind) => kind === e.target.value);
                if (next !== undefined) setOwnerKind(next);
              }}
            />
            <Input
              label={t('orgIssues.createBoard.ownerCode')}
              placeholder={t('orgIssues.createBoard.ownerCodePlaceholder')}
              value={ownerCode}
              onChange={(e) => {
                setOwnerCode(e.target.value);
              }}
            />
          </div>
          {create.isError && (
            <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
              {create.error.message}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" size="sm" loading={create.isPending}>
              {t('orgIssues.createBoard.submit')}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
              {t('orgIssues.common.cancel')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
